import {
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from 'react';
import SignatureCanvas from 'react-signature-canvas';
import DeleteIcon from '../../assets/delete.png';
import { SignaturePadProps, SignaturePadRef } from './signaturePad.type';
import { Trans } from 'react-i18next';
import {
  clearSignature,
  getTrimmedSignature,
  isSignatureEmpty,
  getIdAttributes,
} from '../../utils/helper';

export const idPrefix: string = 'signature-pad';

const SignaturePad = forwardRef<SignaturePadRef, SignaturePadProps>(
  ({ onDelete, initialSignature, handleDrawEnd, style }, ref) => {
    const canvasRef = useRef<SignatureCanvas>(null);
    const [hasDrawn, setHasDrawn] = useState(false);
    const [localSignature, setLocalSignature] = useState<string | undefined>();

    const prefix: string = idPrefix;

    useEffect(() => {
      if (initialSignature) {
        setLocalSignature(initialSignature);
      }
    }, [initialSignature]);

    useImperativeHandle(ref, () => ({
      clear: () => {
        clearSignature({ current: canvasRef.current });
        setHasDrawn(false);
        setLocalSignature(undefined);
      },
      getTrimmedDataURL: () =>
        getTrimmedSignature({ current: canvasRef.current }),
      isEmpty: () => isSignatureEmpty({ current: canvasRef.current }),
    }));

    const handleBegin = () => {
      setLocalSignature(undefined);
      setHasDrawn(true);
    };

    return (
      <div
        className="border-2 border-dashed border-[#E0E0E0] rounded-lg relative mb-4 h-48 flex items-center justify-center bg-[#F9F9FC]"
        style={style}
        {...getIdAttributes(prefix, 'container')}
      >
        {/* If signature already exists, just show image */}
        {localSignature ? (
          <img
            src={localSignature}
            alt="Initial Signature"
            className="max-h-full max-w-full object-contain rounded-lg"
          />
        ) : (
          <>
            <SignatureCanvas
              ref={canvasRef}
              penColor="black"
              onBegin={handleBegin}
              canvasProps={{
                className: 'w-full h-full rounded-lg',
              }}
              onEnd={handleDrawEnd}
            />
            {!hasDrawn && (
              <div className="absolute inset-0 flex items-center justify-center text-[#A3A3A3] text-base pointer-events-none z-0">
                <Trans i18nKey={'onboarding.drawSignature'} />
              </div>
            )}
          </>
        )}

        {!initialSignature && (
          <img
            src={DeleteIcon}
            alt="Delete"
            onClick={onDelete}
            className="w-5 h-5 absolute top-3 right-3 cursor-pointer z-10"
          />
        )}
      </div>
    );
  },
);

export default SignaturePad;
